import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { UnlinkPolicyFromClaimRequest } from '@/domains/policies/types/api/requests/UnlinkPolicyBodyRequest';
import { ProducerEntityType } from '@/domains/producers/types/ProducerEntityType';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { getBenefitValue } from '@/domains/benefits/store/benefitsSlice';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import {
  deleteBenefitSectionDetails,
  deleteClaimSetClaim,
  deleteClaimSetClaimError,
  getBenefitSectionDetails,
  getBenefitSectionDetailsError,
  getBenefitSectionDetailsSuccess,
  getClaimSetClaimListData,
  getClaimSetClaimListDataError,
  getClaimSetClaimListDataSuccess,
  incrementClaimSetClaimListPage,
  startLoadingBenefitSectionDetails,
  startLoadingClaimSetClaimListData,
  unlinkPolicyFromClaim,
  unlinkProducerFromClaim,
  updateClaimSetClaim,
  updateClaimSetClaimFailed,
  updateClaimSetClaimSuccess,
} from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import { selectClaimSetClaimListPagination } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimListSelectors';
import { ClaimOverviewSlots } from '@/enums/ClaimOverviewSlots';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import RecordService from '@/services/RecordService/RecordService';
import SnacksService from '@/services/SnackService/SnackService';

function* updateClaimSetClaimSaga({ payload }: PayloadAction<string | number>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `/v2/${EdsEntityCodes.ClaimEntity}/${payload}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    yield put(updateClaimSetClaimSuccess(normalizedResponse));
  } catch (error) {
    yield put(updateClaimSetClaimFailed());
    console.error(error);
  }
}

function* getClaimSetClaimListSaga({ type, payload }: PayloadAction<{ claimSetId: string }>) {
  try {
    yield put(startLoadingClaimSetClaimListData());

    const pagination = yield select(selectClaimSetClaimListPagination);

    const filters = [{ propertyName: 'ClaimSetId', values: [payload.claimSetId] }];
    const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, includes: [EdsEntityCodes.Milestone, EdsEntityCodes.HelperTags] })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    const claimsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaim) => {
      return {
        ...normalizedClaim,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaim.entities),
        },
      };
    });

    EdsDebugService.logResponse(EdsEntityCodes.ClaimEntity, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecord<ClaimOverviewSlots>(normalizedResponse[0])
        : {},
    });

    yield put(
      getClaimSetClaimListDataSuccess({
        shouldRewrite: type !== incrementClaimSetClaimListPage.type,
        data: {
          listings: claimsWithoutAvailableHelperTags,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    yield put(getClaimSetClaimListDataError);
    console.error(error);
  }
}

function* deleteClaimSetClaimSaga({ payload }: PayloadAction<number | string>) {
  try {
    const claimSetId = yield select(selectCurrentClaimSetId);

    yield call(HttpService.delete, `examiner/claims/${payload}`);

    if (claimSetId) {
      yield put(getClaimSetClaimListData({ claimSetId }));
    }
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Oops! Failed to save the update.',
      content: 'Please try again!',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(deleteClaimSetClaimError());
  }
}

function* getBenefitSectionDetailsSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(startLoadingBenefitSectionDetails({ claimId: payload }));

    const { data } = yield call(
      HttpService.get,
      `/v2/claimEntity/${payload}?includeEntities=Policy,ClaimCoverages.Coverage,Benefit,Benefit.BenefitStatus,Benefit.BenefitAdjustments.Payment.PaymentSchedule,Benefit.BenefitAdjustments.Payment.Payee,Benefit.BenefitStatus,Benefit.BenefitAdjustments.AdjustmentType,HelperTags`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, { includeId: true });

    yield put(getBenefitValue({ claimId: payload }));

    if (data) {
      yield put(
        getBenefitSectionDetailsSuccess({
          claimId: payload,
          benefitSectionData: normalizedResponse,
        }),
      );
    }
  } catch (error) {
    yield put(getBenefitSectionDetailsError);
    console.error(error);
  }
}

function* unlinkPolicyFromClaimSaga({
  payload: { claimId, claimNumber, policyNumber },
}: PayloadAction<UnlinkPolicyFromClaimRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/policies/unlink-from-claim?claimId=${claimId}`, {});

    SnacksService.add({
      type: SnackType.Success,
      title: `Policy ${policyNumber} successfully unlinked from Claim ${claimNumber}.`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    yield put(getBenefitSectionDetails(claimId.toString()));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* deleteBenefitSectionDetailsSaga({ payload }: PayloadAction<{ claimId: string; benefitId: string }>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.delete, `/examiner/benefit/${payload.benefitId}`, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: `Benefit was successfully deleted.`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    yield put(getBenefitSectionDetails(payload.claimId.toString()));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* unlinkProducerFromClaimSaga({
  payload: { claimId, unlinkLevel },
}: PayloadAction<{ claimId: string; unlinkLevel: ProducerEntityType }>) {
  try {
    const claimSetId = yield select(selectCurrentClaimSetId);

    yield put(setSystemLoadingStatus(true));
    yield call(
      HttpService.post,
      `/examiner/producer/unlink-from-claim?claimId=${claimId}&unlinkLevel=${unlinkLevel}`,
      {},
    );

    SnacksService.add({
      type: SnackType.Success,
      title: `Producer successfully unlinked from Claim.`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    if (claimSetId) {
      yield put(getClaimSetClaimListData({ claimSetId }));
    }
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

export function* claimSetClaimListWatcher() {
  yield takeLatest([updateClaimSetClaim.type], updateClaimSetClaimSaga);
  yield takeLatest([deleteClaimSetClaim.type], deleteClaimSetClaimSaga);
  yield takeLatest([getClaimSetClaimListData.type, incrementClaimSetClaimListPage.type], getClaimSetClaimListSaga);
  yield takeEvery([getBenefitSectionDetails.type], getBenefitSectionDetailsSaga);
  yield takeEvery([deleteBenefitSectionDetails.type], deleteBenefitSectionDetailsSaga);
  yield takeLatest([unlinkPolicyFromClaim.type], unlinkPolicyFromClaimSaga);
  yield takeLatest([unlinkProducerFromClaim.type], unlinkProducerFromClaimSaga);
}
